import { useState, useEffect } from 'react'
import { Progress, Spin, Typography } from 'antd'
import { DismissibleTooltip } from '../common/DismissibleTooltip'
import styled from 'styled-components'
import { ProfileService, type CreditQuotaInfo } from '../../services/profile/profileService'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const { Text } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
`

const ProgressContainer = styled.div`
  margin-bottom: ${SPACING.sm};
`

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StatLabel = styled(Text)`
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

const StatValue = styled(Text)`
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

const UnlimitedBadge = styled.div`
  background: var(--color-success-bg);
  color: var(--color-success);
  padding: ${SPACING.xs} ${SPACING.sm};
  border-radius: 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  text-align: center;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACING.lg};
`

const ErrorMessage = styled(Text)`
  color: var(--color-error);
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

function formatCredits(credits: number): string {
  if (credits >= 1000000) {
    return `${(credits / 1000000).toFixed(1)}M`
  }
  if (credits >= 1000) {
    return `${(credits / 1000).toFixed(1)}K`
  }
  return credits.toFixed(0)
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function CreditUsageCard() {
  const [quota, setQuota] = useState<CreditQuotaInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadQuota = async () => {
      try {
        const { data, error } = await ProfileService.getCreditQuota()
        if (error) {
          setError(error.message)
          return
        }
        if (data) {
          setQuota(data)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load credit quota')
      } finally {
        setLoading(false)
      }
    }
    void loadQuota()
  }, [])

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="small" />
      </LoadingContainer>
    )
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>
  }

  if (!quota) {
    return <ErrorMessage>Unable to load credit information</ErrorMessage>
  }

  if (quota.isUnlimited) {
    return (
      <Container>
        <UnlimitedBadge>Unlimited Credits</UnlimitedBadge>
        <StatsRow>
          <StatLabel>Credits Used</StatLabel>
          <StatValue>{formatCredits(quota.creditsUsed)}</StatValue>
        </StatsRow>
        <StatsRow>
          <StatLabel>Period Started</StatLabel>
          <StatValue>{formatDate(quota.periodStart)}</StatValue>
        </StatsRow>
      </Container>
    )
  }

  const usagePercent = quota.creditLimit > 0 ? (quota.creditsUsed / quota.creditLimit) * 100 : 0
  const progressStatus =
    usagePercent >= 90 ? 'exception' : usagePercent >= 75 ? 'normal' : 'success'

  return (
    <Container>
      <ProgressContainer>
        <DismissibleTooltip
          title={`${quota.creditsUsed.toFixed(0)} / ${quota.creditLimit.toFixed(0)} credits`}
        >
          <Progress
            percent={Math.min(usagePercent, 100)}
            status={progressStatus}
            showInfo={false}
            strokeWidth={10}
          />
        </DismissibleTooltip>
      </ProgressContainer>

      <StatsRow>
        <StatLabel>Credits Used</StatLabel>
        <StatValue>
          {formatCredits(quota.creditsUsed)} / {formatCredits(quota.creditLimit)}
        </StatValue>
      </StatsRow>

      <StatsRow>
        <StatLabel>Remaining</StatLabel>
        <StatValue
          style={{
            color:
              quota.creditsRemaining <= 0
                ? 'var(--color-error)'
                : quota.creditsRemaining < quota.creditLimit * 0.1
                  ? 'var(--color-warning)'
                  : undefined,
          }}
        >
          {formatCredits(Math.max(0, quota.creditsRemaining))}
        </StatValue>
      </StatsRow>

      <StatsRow>
        <StatLabel>Period Started</StatLabel>
        <StatValue>{formatDate(quota.periodStart)}</StatValue>
      </StatsRow>
    </Container>
  )
}
